import {
    readPasskeyDeviceHint,
    type PasskeyDeviceHint,
} from './passkey-device-hint';

/**
 * Whether this browser profile holds a passkey we can actually use here, and
 * why not when it does not.
 *
 * A credential is scoped to the host it was created on, so an enrolled hint
 * from another host is real but unusable. Both the step-0 passkey login and the
 * returning-user copy hang off this one answer, so they can never disagree.
 */
export type PasskeyDeviceMatch =
    | { recognised: true; hint: PasskeyDeviceHint }
    | { recognised: false; reason: string };

export const matchPasskeyDeviceToHost = (): PasskeyDeviceMatch => {
    if (typeof window === 'undefined') {
        return { recognised: false, reason: 'rendering on the server' };
    }

    const hint = readPasskeyDeviceHint();
    if (!hint?.enrolled) {
        return {
            recognised: false,
            reason: 'no passkey enrolled from this browser profile',
        };
    }

    const host = window.location.hostname.replace(/^www\./, '');
    if (hint.rpId !== host) {
        // Enrolling on the team server then testing through localhost lands
        // here, as does a passkey made on the other market's domain.
        return {
            recognised: false,
            reason: `the passkey is enrolled for "${hint.rpId}" but this page is "${host}"`,
        };
    }

    return { recognised: true, hint };
};

/** Shorthand for the copy decisions, which only need the yes/no. */
export const isReturningPasskeyDevice = (): boolean =>
    matchPasskeyDeviceToHost().recognised;
